// PRD-13 / ADR-154: a product's photos like a catalogue. Everyone who sees the product can page through them, open
// them full screen and share one with a customer; the owner and the Storekeeper also add, remove and reorder.
import { useEffect, useRef, useState, type ChangeEvent, type PointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Notice } from '../../design/kit';
import { Icon } from '../../design/icons';
import { addProductPhoto, loadProductImages, makeCoverImage, removeProductImage } from '../../data/catalog';
import { messageKey } from '../../lib/errors';
import { NotAnImage, shrinkPhoto } from '../../lib/ux/image';
import { shareProductPhoto, type ShareResult } from '../../lib/ux/sharePhoto';
import { CATALOG, refusalCode, usePhotoLinks } from './common';

export const MAX_PHOTOS = 8;

interface Props {
  product: string;
  name: string;
  /** A line shown with a shared photo, for example the name and the price. */
  caption?: string;
  /** The owner and the Storekeeper manage the photos. */
  canEdit: boolean;
}

export function ProductGallery({ product, name, caption, canEdit }: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const images = useQuery({ queryKey: [...CATALOG, 'gallery', product], queryFn: () => loadProductImages(product) });
  const list = images.data ?? [];
  const links = usePhotoLinks(list.map((i) => i.path));
  const [index, setIndex] = useState(0);
  const [viewer, setViewer] = useState(false);
  const [notice, setNotice] = useState<string>();
  const camera = useRef<HTMLInputElement>(null);
  const picker = useRef<HTMLInputElement>(null);
  const current = list[Math.min(index, Math.max(list.length - 1, 0))];
  const url = current ? links?.get(current.path) : undefined;

  const refresh = () => void qc.invalidateQueries({ queryKey: CATALOG });
  const add = useMutation({
    mutationFn: async (file: File) => {
      const { blob, ext } = await shrinkPhoto(file);
      return addProductPhoto(product, blob, ext);
    },
    onMutate: () => setNotice(undefined),
    onSuccess: () => {
      refresh();
      setIndex(list.length);
    },
    onError: (e) => setNotice(t(e instanceof NotAnImage ? 'photo.notimage' : messageKey(refusalCode(e)))),
  });
  const remove = useMutation({
    mutationFn: (id: string) => removeProductImage(id),
    onMutate: () => setNotice(undefined),
    onSuccess: () => {
      refresh();
      setIndex(0);
    },
    onError: (e) => setNotice(t(messageKey(refusalCode(e)))),
  });
  const cover = useMutation({
    mutationFn: (id: string) => makeCoverImage(id),
    onMutate: () => setNotice(undefined),
    onSuccess: () => {
      refresh();
      setIndex(0);
    },
    onError: (e) => setNotice(t(messageKey(refusalCode(e)))),
  });
  const busy = add.isPending || remove.isPending || cover.isPending;

  const onPick = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) add.mutate(file);
  };

  const share = async (src: string | undefined) => {
    if (!src) return;
    const result: ShareResult = await shareProductPhoto({ url: src, name, text: caption ?? name });
    if (result === 'downloaded') setNotice(t('gallery.downloaded'));
    else if (result === 'failed') setNotice(t('gallery.sharefailed'));
  };

  return (
    <section className="yv-gallery" aria-label={t('gallery.title')} aria-busy={busy || undefined}>
      {current && url ? (
        <button
          type="button"
          className="yv-gallery__main"
          onClick={() => setViewer(true)}
          aria-label={t('gallery.open', { n: index + 1, total: list.length })}
        >
          <img src={url} alt={name} decoding="async" />
          {list.length > 1 && (
            <span className="yv-gallery__count" aria-hidden="true">
              {index + 1} / {list.length}
            </span>
          )}
        </button>
      ) : (
        <div className="yv-gallery__main yv-gallery__main--empty">
          <Icon name="box" />
          <span>{t('photo.none')}</span>
        </div>
      )}

      {list.length > 1 && (
        <ul className="yv-gallery__thumbs" aria-label={t('gallery.thumbs')}>
          {list.map((img, i) => (
            <li key={img.id}>
              <button
                type="button"
                className="yv-gallery__thumb"
                aria-pressed={i === index}
                aria-label={t('gallery.photo', { n: i + 1, total: list.length })}
                onClick={() => setIndex(i)}
              >
                {links?.get(img.path) && <img src={links.get(img.path)} alt="" decoding="async" />}
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="yv-gallery__actions">
        {url && (
          <>
            <Button pill icon="search" onClick={() => setViewer(true)}>
              {t('gallery.fullscreen')}
            </Button>
            <Button pill icon="download" onClick={() => void share(url)}>
              {t('gallery.share')}
            </Button>
          </>
        )}
        {canEdit && (
          <>
            <input
              ref={camera}
              type="file"
              accept="image/*"
              capture="environment"
              hidden
              onChange={onPick}
              data-testid="photo-camera"
            />
            <input ref={picker} type="file" accept="image/*" hidden onChange={onPick} data-testid="photo-gallery" />
            {list.length < MAX_PHOTOS && (
              <>
                <Button pill icon="scan" busy={add.isPending} disabled={busy} onClick={() => camera.current?.click()}>
                  {t('photo.take')}
                </Button>
                <Button pill icon="folder" disabled={busy} onClick={() => picker.current?.click()}>
                  {t('photo.choose')}
                </Button>
              </>
            )}
            {current && index > 0 && (
              <Button pill disabled={busy} onClick={() => cover.mutate(current.id)}>
                {t('gallery.cover')}
              </Button>
            )}
            {current && (
              <Button pill variant="quiet" disabled={busy} onClick={() => remove.mutate(current.id)}>
                {t('photo.remove')}
              </Button>
            )}
          </>
        )}
      </div>
      {canEdit && list.length >= MAX_PHOTOS && <Notice tone="warning">{t('photo.toomany')}</Notice>}
      {notice && <Notice tone="warning">{notice}</Notice>}

      {viewer && current && (
        <Viewer
          name={name}
          items={list.map((i) => ({ id: i.id, src: links?.get(i.path) }))}
          index={Math.min(index, list.length - 1)}
          onIndex={setIndex}
          onClose={() => setViewer(false)}
          onShare={(src) => void share(src)}
        />
      )}
    </section>
  );
}

function Viewer({
  name,
  items,
  index,
  onIndex,
  onClose,
  onShare,
}: {
  name: string;
  items: { id: string; src: string | undefined }[];
  index: number;
  onIndex: (i: number) => void;
  onClose: () => void;
  onShare: (src: string | undefined) => void;
}) {
  const { t } = useTranslation();
  const closeRef = useRef<HTMLButtonElement>(null);
  const start = useRef<number | null>(null);
  const last = items.length - 1;
  const go = (to: number) => onIndex(Math.max(0, Math.min(last, to)));
  // The arrows follow the reading direction, so in Arabic "next" is on the left.
  const rtl = typeof document !== 'undefined' && document.documentElement.dir === 'rtl';

  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') go(index + (rtl ? -1 : 1));
      else if (e.key === 'ArrowLeft') go(index + (rtl ? 1 : -1));
    };
    window.addEventListener('keydown', onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index]);

  const down = (e: PointerEvent) => {
    start.current = e.clientX;
  };
  const up = (e: PointerEvent) => {
    if (start.current === null) return;
    const dx = e.clientX - start.current;
    start.current = null;
    if (Math.abs(dx) < 50) return;
    go(index + (dx < 0 ? 1 : -1) * (rtl ? -1 : 1));
  };

  const item = items[index];
  return (
    <div className="yv-viewer" role="dialog" aria-modal="true" aria-label={t('gallery.title')}>
      <div className="yv-viewer__bar">
        <span className="yv-viewer__title">{name}</span>
        <span className="yv-viewer__count">
          {index + 1} / {items.length}
        </span>
        <button
          type="button"
          className="yv-viewer__btn"
          onClick={() => onShare(item?.src)}
          aria-label={t('gallery.share')}
        >
          <Icon name="download" />
        </button>
        <button
          ref={closeRef}
          type="button"
          className="yv-viewer__btn"
          onClick={onClose}
          aria-label={t('gallery.close')}
        >
          <Icon name="close" />
        </button>
      </div>
      <div
        className="yv-viewer__stage"
        onPointerDown={down}
        onPointerUp={up}
        onPointerCancel={() => (start.current = null)}
      >
        {item?.src && <img src={item.src} alt={name} draggable={false} />}
      </div>
      {items.length > 1 && (
        <div className="yv-viewer__nav">
          <button
            type="button"
            className="yv-viewer__btn"
            disabled={index === 0}
            onClick={() => go(index - 1)}
            aria-label={t('gallery.prev')}
          >
            <Icon name="back" />
          </button>
          <button
            type="button"
            className="yv-viewer__btn"
            disabled={index === last}
            onClick={() => go(index + 1)}
            aria-label={t('gallery.next')}
          >
            <Icon name="chevron" />
          </button>
        </div>
      )}
    </div>
  );
}
